import * as React from "react";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { DiscoveryCycleCoverage } from "../../models/Statistics/DiscoveryCycle";
import { MigrationType, MigrationTypeNouns } from "../../models/Statistics/MigrationType";

/**
 * A cycle's coverage verdict, as the one badge every place that shows a cycle uses. The verdict rather than the bare
 * degraded flag, because the flag puts the same amber on a cycle whose only trouble was an unreadable share and on
 * one that lost track of what it had checked, while clearing carries on for the first and stops for the second.
 *
 * A cycle whose client cannot prove coverage either way gets no verdict; it still says "Degraded" when the client
 * noticed something, since that is all there is to say. A draining cycle gets nothing here — its "Reports
 * pending" badge already says it is not settled.
 */
export const CoverageBadge: React.FC<{
  coverage: DiscoveryCycleCoverage;
  degraded: boolean;
  migrationType: MigrationType;
  rootsUnreachable: number;
}> = ({ coverage, degraded, migrationType, rootsUnreachable }) => {
  const nouns = MigrationTypeNouns[migrationType] ?? { one: "record", many: "records" };

  switch (coverage) {
    case DiscoveryCycleCoverage.Complete:
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="success">
              <CheckCircle2 className="size-3" />
              Complete
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            This cycle saw every {nouns.one} the source offered, so links nobody reported can safely be cleared out.
          </TooltipContent>
        </Tooltip>
      );

    case DiscoveryCycleCoverage.Bounded:
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="warning">
              <Info className="size-3" />
              Bounded
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            {rootsUnreachable > 0
              ? `Something went wrong, but only where this cycle can say: ${rootsUnreachable === 1 ? "a root" : `${rootsUnreachable} roots`} could not be read. Links beneath ${rootsUnreachable === 1 ? "it" : "them"} are kept as they are; everything else is cleared out as usual.`
              : `Something went wrong, but only where this cycle can say: some ${nouns.many} were passed over, and they are kept as they are. Everything else is cleared out as usual.`}
          </TooltipContent>
        </Tooltip>
      );

    case DiscoveryCycleCoverage.Unbounded:
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="danger">
              <AlertTriangle className="size-3" />
              Unbounded
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            Something went wrong on this cycle that it cannot pin down, so nothing it did not see is cleared out.
          </TooltipContent>
        </Tooltip>
      );

    case DiscoveryCycleCoverage.Unproven:
      return degraded ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="warning">
              <AlertTriangle className="size-3" />
              Degraded
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            Something went wrong during this cycle, so these totals are not the whole picture.
          </TooltipContent>
        </Tooltip>
      ) : null;

    default:
      return null;
  }
};

export default CoverageBadge;
